import { useBrowserLoginForm } from "@/shared/browserLogin.ts";
import type { BrowserLoginHold } from "@/types/browserLogin.ts";
import { browserLoginClient } from "./browserLoginClient.ts";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Button } from "@/components/ui/button.tsx";
import { LockKeyhole, MousePointer2 } from "lucide-react";
import { cn } from "@/lib/utils.ts";

export function BrowserLoginForm({
  origin,
  hold,
  onSaved,
  onManual,
  onCancel,
}: {
  origin: string;
  hold?: BrowserLoginHold;
  onSaved: () => void;
  onManual?: () => void;
  onCancel?: () => void;
}) {
  const { form, save, forget, metadata, pending, submitLabel } = useBrowserLoginForm(
    browserLoginClient,
    origin,
    hold,
    onSaved,
  );
  return (
    <form
      className="w-full space-y-3 py-2"
      data-private="true"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <div className="space-y-1 pb-1">
        <p className="text-sm text-muted-foreground">Sign in to</p>
        <h2 className="break-all text-lg font-semibold leading-snug">{origin}</h2>
      </div>
      <form.Field name="username">
        {(field) => (
          <div className="space-y-1">
            <Label htmlFor="browser-login-username">Username or email</Label>
            <Input
              id="browser-login-username"
              name="username"
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              data-private="true"
              required
              disabled={pending}
              className="h-11 rounded-xl text-base"
            />
          </div>
        )}
      </form.Field>
      <form.Field name="password">
        {(field) => (
          <div className="space-y-1">
            <Label htmlFor="browser-login-password">Password</Label>
            <Input
              id="browser-login-password"
              name="password"
              type="password"
              value={field.state.value}
              onChange={(event) => field.handleChange(event.target.value)}
              autoComplete="current-password"
              data-private="true"
              required
              disabled={pending}
              className="h-11 rounded-xl text-base"
            />
          </div>
        )}
      </form.Field>
      {save.isError || forget.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn’t save this change. Refresh the form and try again.
        </p>
      ) : null}
      {metadata.isError ? (
        <p role="alert">
          Saved login is unavailable.{" "}
          <Button
            type="button"
            variant="link"
            data-statsig-name="browser-login-refresh"
            onClick={() => void metadata.refetch()}
          >
            Refresh
          </Button>
        </p>
      ) : null}
      <p className="flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground">
        <LockKeyhole aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        Save securely for future sign-ins to this site.
      </p>
      <div className={cn("flex gap-2", onManual ? "flex-col" : "flex-wrap")}>
        <Button
          data-statsig-name="browser-login-save"
          type="submit"
          variant="primary"
          className="min-h-11 rounded-xl"
          disabled={pending || !metadata.data || metadata.isError}
        >
          {submitLabel}
        </Button>
        {onManual ? (
          <Button
            type="button"
            variant="outlinePopover"
            className="min-h-11 gap-2 rounded-xl"
            data-statsig-name="browser-login-manual"
            disabled={pending}
            onClick={onManual}
          >
            <MousePointer2 aria-hidden className="size-4" />
            Sign in manually
          </Button>
        ) : null}
        {onCancel ? (
          <Button
            type="button"
            variant="ghost"
            data-statsig-name="browser-login-cancel"
            disabled={pending}
            onClick={onCancel}
          >
            Cancel request
          </Button>
        ) : null}
        {!hold && metadata.data?.itemId ? (
          <Button
            data-statsig-name="browser-login-forget"
            type="button"
            variant="ghost"
            disabled={pending}
            onClick={() => {
              form.reset();
              forget.mutate();
            }}
          >
            Forget saved login
          </Button>
        ) : null}
      </div>
    </form>
  );
}
